// Analyse d'une affaire : décomposition de la dérive, avancement des coûts, gains et pertes achats.

import {
  ACTION_LABELS,
  CAUSE_LABELS,
  DATE_LABELS,
  eur,
  eurSigned,
  int,
  type Affaire,
  type Decomposition,
  type PlanRow,
  type Saving,
  isAffaire,
} from '@cockpit/shared';
import { Badge, Drawer, Table, Td, Th, cn } from '@cockpit/ui';
import { DivergingBars } from '../charts.js';
import { DoneButton, PrioBadge, entLabel, shortDate } from './bits.js';
import type { TraitementDto } from '@cockpit/shared';

const tone = (v: number) => (v < 0 ? 'text-success-700' : v > 0 ? 'text-danger-700' : 'text-ink-3');

function Label({ children }: { children: React.ReactNode }) {
  return <p className="mb-2 font-display text-[12px] uppercase tracking-[0.18em] text-ink-3">{children}</p>;
}

export function DriftExplain({ r, d }: { r: Affaire; d: Decomposition }) {
  const pts = r.mr - r.mp;
  return (
    <div className="flex flex-col gap-5">
      <div>
        <Label>
          Pourquoi la marge {r.der < 0 ? 'baisse' : 'monte'} · {eurSigned(r.der)}, {pts > 0 ? '+' : '−'}
          {Math.abs(pts).toLocaleString('fr-FR', { maximumFractionDigits: 1 })} pt
        </Label>
        <DivergingBars
          fmt={eurSigned}
          rows={[
            { label: 'Achats', value: d.dA },
            { label: 'MO + ST', value: d.dM },
            { label: 'Autres charges', value: d.dO },
          ]}
        />
        <ul className="mt-3 flex list-disc flex-col gap-1 pl-5 text-[14px] text-ink">
          {d.why.map((w) => (
            <li key={w}>{w.charAt(0).toUpperCase() + w.slice(1)}</li>
          ))}
          {d.comp.length > 0 && r.der < 0 && (
            <li className="text-ink-3">
              Compensé en partie par : {d.comp.map((c) => CAUSE_LABELS[c].toLowerCase()).join(', ')}
            </li>
          )}
        </ul>
      </div>
      <div>
        <div className="mb-2 flex flex-wrap items-center gap-2">
          <Label>Où en est le chantier</Label>
          <Badge tone={d.ratt ? 'success' : 'neutral'}>{d.ratt ? 'Rattrapable' : 'Dérive constatée'}</Badge>
          {d.dstat === 'ok' && <Badge tone="neutral">fin dans {d.jr} j</Badge>}
          {d.dstat === 'past' && (
            <Badge tone={r.cr >= 90 ? 'neutral' : 'warn'}>
              {d.jr === 0
                ? 'fin prévue aujourd’hui'
                : r.cr >= 90
                  ? `terminé depuis ${-(d.jr ?? 0)} j`
                  : `fin dépassée de ${-(d.jr ?? 0)} j, date à vérifier`}
            </Badge>
          )}
          {d.dstat === 'nofin' && <Badge tone="warn">pas de date de fin</Badge>}
        </div>
        <Table minWidth={480}>
          <thead>
            <tr>
              <Th />
              <Th align="right">Prévu</Th>
              <Th align="right">Revu</Th>
              <Th align="right">Réalisé</Th>
              <Th align="right">Reste à engager</Th>
            </tr>
          </thead>
          <tbody>
            <tr>
              <Td className="font-semibold">Achats</Td>
              <Td align="right">{eur(d.aP)}</Td>
              <Td align="right">{eur(d.aR)}</Td>
              <Td align="right">{eur(d.aX)}</Td>
              <Td align="right">{eur(d.resteA)}</Td>
            </tr>
            <tr>
              <Td className="font-semibold">MO + sous-traitance</Td>
              <Td align="right">{eur(d.mP)}</Td>
              <Td align="right">{eur(d.mR)}</Td>
              <Td align="right">{eur(d.mX)}</Td>
              <Td align="right">{eur(d.resteM)}</Td>
            </tr>
          </tbody>
        </Table>
        <p className="mt-3 rounded-sm bg-surface-2 px-3 py-2 text-[13.5px] text-ink-2">
          {r.der >= 0
            ? 'Marge stable ou en hausse : vérifier que le budget révisé est complet.'
            : d.ratt
              ? `Il reste ${d.rpct} % des coûts à engager : renégocier les achats restants, cadrer les heures, chercher un avenant.`
              : 'Coûts presque tous engagés : chercher un avenant ou une réclamation, et faire le retour d’expérience sur le chiffrage.'}
        </p>
      </div>
    </div>
  );
}

export function SavingDetail({ s }: { s: Saving }) {
  const by = Object.entries(s.by).sort((a, b) => a[1] - b[1]);
  return (
    <div>
      <Label>Achats · gains et pertes (OD achat saving)</Label>
      <div className="mb-3 flex flex-wrap gap-x-5 gap-y-1 text-[13.5px]">
        <span>
          Net <b className={cn('tnum', tone(s.net))}>{eurSigned(s.net)}</b>
        </span>
        <span>
          Gains <b className="tnum text-success-700">{eurSigned(s.gain)}</b>
        </span>
        <span>
          Pertes <b className="tnum text-danger-700">{eurSigned(s.perte)}</b>
        </span>
        <span className="text-ink-3">
          {s.n} ligne{s.n > 1 ? 's' : ''} de commande
        </span>
      </div>
      <div className="mb-3 flex flex-wrap gap-1.5">
        {by.map(([c, v]) => (
          <span
            key={c}
            className="inline-flex items-center gap-1.5 rounded-sm border border-line-soft bg-surface-2 px-2 py-0.5 text-[12.5px] text-ink-2"
          >
            {c}
            <b className={cn('tnum font-semibold', tone(v))}>{eurSigned(v)}</b>
          </span>
        ))}
      </div>
      <Table minWidth={600}>
        <thead>
          <tr>
            <Th>Cause</Th>
            <Th>Fournisseur · commande</Th>
            <Th>Commentaire</Th>
            <Th align="right">Montant HT</Th>
            <Th align="right">Écart</Th>
          </tr>
        </thead>
        <tbody>
          {s.lines.map((l, i) => (
            <tr key={i}>
              <Td>{l.c}</Td>
              <Td>
                {l.f ?? '—'}
                <div className="tnum text-[12px] text-ink-3">
                  {[l.p, shortDate(l.d), l.st].filter(Boolean).join(' · ')}
                </div>
              </Td>
              <Td className="max-w-[220px] text-ink-2">{l.m ?? ''}</Td>
              <Td align="right">{int(l.ht)} €</Td>
              <Td align="right" className={cn('font-semibold', tone(l.v))}>
                {eurSigned(l.v)}
              </Td>
            </tr>
          ))}
        </tbody>
      </Table>
      <p className="mt-2 text-[12px] text-ink-3">
        Écart au chiffrage par ligne : négatif = achat moins cher que prévu (gain).
      </p>
    </div>
  );
}

export function AffaireDrawer({
  row,
  onClose,
  done,
  info,
  onToggle,
}: {
  row: PlanRow | null;
  onClose: () => void;
  done: boolean;
  info?: TraitementDto;
  onToggle: () => void;
}) {
  if (!row) return null;
  const a = isAffaire(row) ? row : null;
  return (
    <Drawer
      open
      onClose={onClose}
      width={720}
      title={row.nom ?? row.cli ?? row.code}
      subtitle={
        <span className="flex flex-wrap items-center gap-x-2 gap-y-1">
          <span className="tnum">{row.code}</span>·<span>{entLabel(row.ent)}</span>
          {row.type && <Badge tone="azur">{row.type}</Badge>}
          {a && a.acts.length > 0 && <PrioBadge prio={a.prio} />}
          <span>· Client : {row.cli ?? '—'}</span>
        </span>
      }
      footer={<DoneButton done={done} info={info} onToggle={onToggle} size="md" />}
    >
      <div className="flex flex-col gap-8">
        {a && (
          <dl className="grid grid-cols-2 gap-x-6 gap-y-3 text-[13.5px] sm:grid-cols-4">
            {[
              ['CA prévu', eur(a.ca)],
              ['Facturé', `${a.fa} %`],
              ['Coûts engagés', `${a.cr} %`],
              ['Marge', `${a.mp.toLocaleString('fr-FR')} % → ${a.mr.toLocaleString('fr-FR')} %`],
              ['Début', shortDate(a.deb) || '—'],
              ['Fin', shortDate(a.fin) || 'non renseignée'],
              ['Commercial', a.com ?? '—'],
              ['Conducteur', a.cond ?? 'non affecté'],
            ].map(([k, v]) => (
              <div key={k}>
                <dt className="text-[12px] text-ink-3">{k}</dt>
                <dd className="tnum font-semibold text-ink">{v}</dd>
              </div>
            ))}
          </dl>
        )}
        {a && a.acts.length > 0 && (
          <div>
            <Label>Actions à mener</Label>
            <ul className="divide-y divide-line-soft rounded-md border border-line-soft">
              {a.acts.map((x) => (
                <li key={x.k} className="flex items-start justify-between gap-4 px-4 py-2.5">
                  <div>
                    <p
                      className={cn(
                        'text-[12px] font-semibold uppercase tracking-[0.06em]',
                        x.w || x.k === 'loss' ? 'text-danger-700' : 'text-ink-3',
                      )}
                    >
                      {ACTION_LABELS[x.k]}
                    </p>
                    <p className="text-[14px] text-ink">{x.txt}</p>
                  </div>
                  <span className="tnum shrink-0 text-[13px] font-semibold text-ink-2">{eur(x.e)}</span>
                </li>
              ))}
            </ul>
          </div>
        )}
        {a && a.dates.length > 0 && (
          <div>
            <Label>Dates à vérifier</Label>
            <ul className="flex flex-col gap-1.5 text-[14px]">
              {a.dates.map((x) => (
                <li key={x.k}>
                  <span className="mr-2 text-[12px] font-semibold uppercase tracking-[0.06em] text-warn-700">
                    {DATE_LABELS[x.k]}
                  </span>
                  {x.txt}
                </li>
              ))}
              {a.est && (
                <li>
                  <span className="mr-2 text-[12px] font-semibold uppercase tracking-[0.06em] text-navy-600">
                    Fin estimée
                  </span>
                  vers le <b className="tnum">{shortDate(a.est)}</b> au rythme actuel des coûts.
                </li>
              )}
            </ul>
          </div>
        )}
        {a?.dec ? (
          <DriftExplain r={a} d={a.dec} />
        ) : (
          a && <p className="text-[13.5px] text-ink-3">Marge inchangée entre prévu et révisé.</p>
        )}
        {row.sav && <SavingDetail s={row.sav} />}
      </div>
    </Drawer>
  );
}
